import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

const StepCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
}));

function StepHeader({ index, title, done, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {done ? <CheckCircleRoundedIcon sx={{ color: 'primary.main' }} /> : <RadioButtonUncheckedRoundedIcon sx={{ color: 'text.disabled' }} />}
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
          {index}. {title}
        </Typography>
      </Stack>
      <Button size="small" sx={{ textTransform: 'none', minWidth: 0 }}>
        {action}
      </Button>
    </Stack>
  );
}

export default function VersionsView() {
  return {
    main: (
      <MainCard>
        <Stack direction={{ xs: 'column', lg: 'row' }} alignItems="stretch">
          <Box sx={{ width: { xs: '100%', lg: 260 }, borderRight: { lg: '1px solid' }, borderColor: { lg: 'divider' } }}>
            <Box sx={{ bgcolor: 'background.neutral', px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Versions
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No versions created yet.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <LayersRoundedIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 24, md: 30 } }}>
                Versions
              </Typography>
            </Stack>

            <Stack spacing={3.5}>
              <Box>
                <StepHeader index="1" title="Source Images" done action="Edit" />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} sx={{ pl: 4.5, pt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">Images: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>1</Box></Typography>
                  <Typography variant="body2" color="text.secondary">Classes: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>2</Box></Typography>
                  <Typography variant="body2" color="text.secondary">Unannotated: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>0</Box></Typography>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <StepHeader index="2" title="Train/Test Split" done action="Edit" />
                <Stack spacing={0.8} sx={{ pl: 4.5, pt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">Training Set: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>1 images</Box></Typography>
                  <Typography variant="body2" color="text.secondary">Validation Set: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>images</Box></Typography>
                  <Typography variant="body2" color="text.secondary">Testing Set: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>images</Box></Typography>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <StepHeader index="3" title="Preprocessing" done={false} action="Edit" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4.5, pt: 0.8 }}>
                  Decrease training time and increase performance by applying image transformations to all images in this dataset.
                </Typography>
                <StepCard sx={{ ml: 4.5, mt: 2, maxWidth: 340 }}>
                  <Box sx={{ px: 2, py: 1.8 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Auto-Orient</Typography>
                      <Button size="small" sx={{ textTransform: 'none' }}>Edit</Button>
                    </Stack>
                  </Box>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.8 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Resize</Typography>
                        <Typography variant="body2" color="text.secondary">Stretch to 512x512</Typography>
                      </Box>
                      <Button size="small" sx={{ textTransform: 'none' }}>Edit</Button>
                    </Stack>
                  </Box>
                  <Divider />
                  <Button startIcon={<AddCircleRoundedIcon />} sx={{ justifyContent: 'flex-start', width: '100%', px: 2, py: 1.4, textTransform: 'none' }}>
                    Add Preprocessing Step
                  </Button>
                </StepCard>
                <Stack direction="row" spacing={1.5} sx={{ pl: 4.5, pt: 2 }}>
                  <Button color="inherit" sx={{ textTransform: 'none' }}>Back</Button>
                  <Button variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}>Continue</Button>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <StepHeader index="4" title="Augmentation" done={false} action="Edit" />
              </Box>

              <Divider />

              <Box>
                <StepHeader index="5" title="Create" done={false} action="" />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </MainCard>
    ),
    side: null,
  };
}
